import React, { forwardRef, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  DialogContent,
  Box,
  styled,
  Avatar,
  AvatarGroup,
  CircularProgress 
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import { motion } from "framer-motion";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import Feed from "../feed";
import { DOMAIN } from "../../config";
import CustomizedProgressBars from "../common/loading";
import SeriesMenuDialog from "./seriesMenu";
import { useUser } from '../../contexts/userContext';
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import BloggerListDialog from "./blogerListDialog";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="right" ref={ref} {...props} />;
});

export default function FollowDialog({
  openSeries = false,
  setOpenSeries,
  matches,
  chipTitle,
}) {
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [openSeriesMenu, setOpenSeriesMenu] = useState(false);
  const touchStartRef = useRef(0);
  const { userData } = useUser();
  const [avatars, setAvatars] = useState([]);
  const [openList, setOpenList] = useState(false);

  const handleTouchStart = (event) => {
    event.stopPropagation();
    touchStartRef.current = event.touches[0].clientX;
  };

  const handleSwipe = (event) => {
    const touchEndX = event.changedTouches[0].clientX;
    const swipeDistance = touchStartRef.current - touchEndX;
    const flag =
      event.target.className.includes("MuiBox-root") ||
      event.target.className.includes("MuiTypography-root") ||
      event.target.className.includes("swiper-slide") ||
      event.target.className === "";

    if (swipeDistance > 120 && !flag) {
      setOpenSeries(false);
    }
  };

  const handleClose = () => setOpenSeries(false);

  /** ✅ 打开时加载第一页内容 */
  useEffect(() => {
    if (!openSeries) return;

    setLoading(true);
    fetch(`${DOMAIN}follow/userFeed?userId=${userData.id}&page=1&limit=5`)
      .then((res) => res.json())
      .then((data) => {
        setLoading(false);
        if (!data || data.length === 0) {
          setEmpty(true);
          setAvatars([]);
          setLatest([]);
        } else {
           // ✅ 提取前4个元素中第一个 medias 的 fileid
          const avatarUrls = data
            .slice(0, 4)
            .map((item) => item?.medias?.[0]?.fileid)
            .filter(Boolean) // 过滤空值
            .map((fileid) => `${DOMAIN}miniapp/${fileid}.tb.jpg`); // ✅ 拼接成图片URL

          setAvatars(avatarUrls);
          setEmpty(false);
          setLatest(data);
        }
      })
      .catch(() => setLoading(false));
  }, [openSeries]);

  return (
    <Box>
      <BloggerListDialog  open={openList} onClose={() => setOpenList(false)} />
      <SeriesMenuDialog
        openSeriesMenu={openSeriesMenu}
        setOpenSeriesMenu={setOpenSeriesMenu}
        matches={matches}
        chipTitle="系列菜单"
      />

      <Dialog
        fullScreen
        open={openSeries}
        onClose={handleClose}
        TransitionComponent={Transition}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleSwipe}
        PaperProps={{
          onTouchStart: handleTouchStart,
          onTouchEnd: handleSwipe,
        }}
      >
        {/* 顶部栏 */}
        {/* ✅ 顶部栏调用 */}
      
      <CustomAppBar
        chipTitle={chipTitle}
        avatars={avatars}
        loading={loading}
        empty={empty}
        onFollowListClick={() => console.log("点击关注列表")}
        handleClose={handleClose}
        handleList={setOpenList}
      />
       

        {/* 内容区 */}
        <DialogContent
          style={{
            padding: 0,
            paddingTop: 45,
            overflowY: "auto",
            position: "relative",
          }}
        >
          {latest.length > 0 && (
            <Feed
              matches={matches}
              latest={latest}
              setLatest={setLatest}
              fetchUrl={`${DOMAIN}follow/userFeed?userId=${userData.id}`}
              remark={false}
              title="系列专辑"
            />
          )}

          {empty && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexFlow: "row",
                py: 20,
                color: "#999",
              }}
            >
              <SearchOffIcon />
              <Typography variant="h6" fontSize={14}>
                没有此内容，我们会尽快发布...
              </Typography>
            </Box>
          )}

          {loading && <CustomizedProgressBars />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

const BlinkingButton = styled(Button)({
  animation: "blinking 1.8s infinite",
  "@keyframes blinking": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.7 },
    "100%": { opacity: 1 },
  },
});



function CustomAppBar({
  handleClose,
  chipTitle,
  handleList,
  onFollowListClick,
  avatars = [],
  loading = false,
  empty = false,
}) {
  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        backgroundColor: "rgba(255,255,255,0.25)",
        color: "#000",
      }}
    >
      <Toolbar sx={{ minHeight: 45 }}>
        <motion.div
          initial={{ x: "100vw", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
          }}
        >
          {/* 左侧关闭按钮 */}
          <IconButton edge="start" color="inherit" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>

          {/* 标题 */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 500, fontSize: 16, flex: 1 }}
          >
            {chipTitle}
          </Typography>

          {/* ✅ 如果正在加载 */}
          {loading && <CircularProgress size={22} color="inherit" />}

          {/* ✅ 显示头像组（可点击） */}
          {!loading && avatars.length > 0 && (
            <Box
              sx={{ cursor: "pointer", display: "flex" }}
              onClick={()=>handleList(true)}
            >
              <AvatarGroup
                total={avatars.length}
                sx={{
                  "& .MuiAvatar-root": {
                    width: 28,
                    height: 28,
                    border: "1px solid #fff",
                  },
                }}
              >
                {avatars.map((src, index) => (
                  <Avatar key={index} src={src} alt={`user-${index}`} />
                ))}
              </AvatarGroup>
            </Box>
          )}

          {/* ✅ 如果为空 */}
          {!loading && empty && (
            <Typography variant="body2" color="text.secondary">
              暂无关注
            </Typography>
          )}
        </motion.div>
      </Toolbar>
    </AppBar>
  );
}